import React from 'react';
import DeadFish from '../../../../components/Operation/DeadFish';
import LiceList from "../../../../components/Operation/LiceList";
import { WeatherFull } from '../../../../components/Operation/Weather';
import { Approve } from '../../../../components/Popup/PopupOpInfo/_component/Approver/Approver2';
import AttachFile from "../../../../components/Popup/PopupOpInfo/_component/AttachFile";
import HarvestInfo from "../../../../components/Popup/PopupOpInfo/Info/Harvest/HarvestInfo";
import { useSelector } from '../../../../util/store/store';
import { InfoMode, OP_STATUS, STEP_KEY_V2 } from "../../../../util/varibles/constants";
import { formatNumber } from "../../../../util/varibles/global";
import { IHarvest2, IOperation2 } from "../../../../util/varibles/interface";
import { getTotalAmountAndTotalWeight } from "../../../PlanOperationPage/util";
import Checklists from "../../_components/Checklists";
import FishDisease from "../../_components/FishDisease";
import Operation from "../../_components/Operation";
import stylesOp from '../../_components/Operation/style.module.scss';
import OperationCost from '../../_components/OperationCost';
import OperationTime from '../../_components/OperationTime';
import Vessel from "../../_components/Vessel";
import ViewNote from "../../_components/ViewNote";
import { selectOpDetail } from '../../constants';
import stylesContainer from "../../style.module.scss";
import ActivityLog from './ActivityLog';
import Route from '../../_components/Route';

interface IProps {
    data: IOperation2<IHarvest2>
    isOwn: boolean
}

const Harvest: React.FC<IProps> = ({ data, isOwn }) => {
    const infoMode = useSelector(selectOpDetail.infoMode);
    const { operation, activity_log } = data;
    const { sub_operations, note = '', summary = [] } = operation;
    const { total_amount, total_weight } = getTotalAmountAndTotalWeight(sub_operations);
    const actualTotal = summary.reduce((rs, item) => {
        const { actual } = item;
        rs.fishAmount += actual.fish_amount;
        rs.totalWeight += actual.total_weight;
        return rs;
    }, { fishAmount: 0, totalWeight: 0 })
    const totalDischargedFish = summary.reduce((unitTotal, item) => {
        return unitTotal + item.actual.tanks.reduce((tankTotal, tank) => {
            return tankTotal + tank.discharge.reduce((dischargeTotal, discharge) => {
                return dischargeTotal + discharge.fish_amount;
            }, 0);
        }, 0);
    }, 0);
    const totalDeadFish = operation.dead_fish ?? (activity_log.reduce((total, step) => {
        if (step.key !== STEP_KEY_V2.DISCHARGING)
            return total;

        return total + (step.real_sub_steps || []).reduce((stepTotal, subStep) => {
            return stepTotal + (subStep.dead_fish || 0);
        }, 0);
    }, 0) || undefined);
    const displayFishAmount = infoMode === InfoMode.PLAN ? total_amount : actualTotal.fishAmount;
    const displayTotalWeight = infoMode === InfoMode.PLAN ? total_weight : actualTotal.totalWeight;
    const capacityPercent = displayTotalWeight && data.vessel?.capacity
        ? Math.round(displayTotalWeight / data.vessel.capacity * 100)
        : null;

    const isNote = note.length === 0;
    const { status, approver, files = [] } = operation;

    return <div className='col-span-2 pb-3'>
        <div className='grid grid-cols-1 items-stretch gap-[15px] min-[901px]:grid-cols-[minmax(0,2fr)_minmax(290px,1fr)] mb-[15px] '>
            <div className='min-w-0 [&>div]:!mb-0 [&>div]:!h-full [&>div]:w-full'>
                <Operation {...{ isNote }}>
                    <div className='[&>div:not([data-empty=true])]:!h-[24px] [&>div:not([data-empty=true])]:!min-h-[24px] [&>div:not([data-empty=true])]:!max-h-[24px] [&>div]:items-center [&_.icon]:!max-h-[16px] [&_.icon]:!max-w-[16px]'>
                        <OperationTime activity_log={activity_log} mode={infoMode} />
                        <WeatherFull operation={data.operation} />
                        <div className={stylesOp['info-line']} data-empty={files.length === 0}>
                            <AttachFile files={files} />
                        </div>
                        <FishDisease diseases={operation.diseases || []} units={operation.sub_operations} />
                        <LiceList diseases={operation.diseases || []} />
                        <Approve approver={approver} status={status} />
                        <div className={stylesOp['info-line']}>
                            <span>∑ weight:</span>
                            <span>
                                {displayFishAmount && displayTotalWeight && capacityPercent !== null
                                    ? `${formatNumber(displayFishAmount)} | ${formatNumber(Math.round(displayTotalWeight / 1000) / 1000)} tn (${capacityPercent}% capacity)`
                                    : '--'}
                            </span>
                        </div>
                    </div>
                    <div className='mt-5'>
                        <HarvestInfo operation={operation as IHarvest2} infoMode={infoMode} />
                    </div>
                    <ViewNote data={data} isOwn={isOwn} note={note} />
                </Operation>
            </div>
            <div className='grid min-w-0 grid-rows-[auto_minmax(0,1fr)] gap-[15px] [&>div]:mb-0 [&>div]:w-full [&>div:last-child]:!h-full'>
                <div className={stylesContainer.section}>
                    <div className={stylesContainer['header-box']}>
                        <span className='text-[15px] font-[600]'>Harvest grading</span>
                    </div>
                    <div className='flex flex-col items-center [&>div]:h-[80px] [&>div]:justify-center'>
                        <DeadFish
                            value={totalDeadFish}
                            total={totalDischargedFish}
                            valueClassName='text-[20px]'
                            layout='vertical'
                            label='Σ Dead fish'
                            showPercent={operation.status === OP_STATUS.FINISHED}
                        />
                    </div>
                </div>
                <Checklists />
            </div>
        </div>
        <div className='min-w-0 [&>div]:w-full mb-[15px]'>
            <OperationCost operation={data.operation} vesselId={data.vessel.id} />
        </div>
        <div className='min-w-0 [&>div]:w-full mb-[15px]'>
            <Vessel />
        </div>
        <div className={`mb-[15px]`}>
            <div className="flex w-full align-items-center justify-center">
                <span className="my-2 text-[20px]">Activity log</span>
            </div>
            <ActivityLog data={data} />
        </div>
        <Route />
    </div>
};

export default Harvest;
